<template lang="pug">
  div(ref="group" :class="classes.group")
    ui-label(
      v-if="label"
      :class="[classes.label, labelClass]"
      :disabled="disabled"
    ) {{ label }}
    div(ref="container" :class="classes.root" @click="onClickContainer")
      ui-input-text(
        v-if="editable"
        :aria-activedescendant="focused ? focusedOptionId : undefined"
        :aria-controls="$id + '_list'"
        :aria-expanded="overlayVisible"
        aria-haspopup="listbox"
        :aria-invalid="invalid || undefined"
        :aria-label="ariaLabel"
        :aria-lablledby="ariaLabelledBy"
        autocomplete="off"
        :disabled="disabled"
        :class="[classes.input, inputClass]"
        :id='inputId'
        type="text"
        ref="focusInput"
        role="combobox"
        :style="[inputStyle]"
        :tabIndex="!disabled? tabindex : -1"
        :value="editableInputValue"
        @blur="onBlur"
        @focus="onFocus"
        @input="onEditableInput"
        @keydown="onKeyDown"
      )
      span(
        v-else
        :aria-activedescendant="focused ? focusedOptionId : undefined"
        :aria-controls="$id + '_list'"
        :aria-expanded="overlayVisible"
        aria-haspopup="listbox"
        :aria-invalid="invalid || undefined"
        :aria-label="ariaLabel"
        :aria-labelledby="ariaLabelledBy"
        ref="focusInput"
        :id="inputId"
        :class="[classes.value]"
        :style="[inputStyle]"
        :tabindex="!disabled ? tabindex : -1"
        role="combobox"
        @blur="onBlur"
        @focus="onFocus"
        @keydown="onKeyDown"
      )
        slot(name="value" :value="d_value" :placeholder="placeholder") {{ displayValue === 'EMPTY_LABEL' ? '&nbsp;' : (displayValue ?? 'empty') }}
      //-TODO: Handle loading icon?
      div(class='select-icon')
        LoadingIcon(v-if="loading" :spin="true")
        dropdown-icon(v-else size="1rem")
      ui-portal(:appendTo="appendTo")
        transition(
          name="ui-connected-overlay"
          @enter="onOverlayEnter"
          @after-enter="onOverlayAfterEnter"
          @leave="onOverlayLeave"
          @after-leave="onOverlayAfterLeave"
        )
          div(
            v-if="overlayVisible"
            :class="[classes.overlay, panelClass, overlayClass]"
            :ref="overlayRef"
            :style="[panelStyle, overlayStyle]"
            @click="onOverlayClick"
            @keydown="onOverlayKeyDown"
          )
            span(
              aria-hidden="true"
              class="ui-hidden-accessible ui-hidden-focusable"
              :data-ui-hidden-accessible="true"
              :data-ui-hidden-focusable="true"
              ref="firstHiddenFocusableElementOnOverlay"
              role="presentation"
              :tabindex="0"
              @focus="onFirstHiddenFocus"
            )
            slot(
              name="header"
              :value="d_value"
              :options="visibleOptions"
            )
            div(v-if="filter" :class="classes.header")
              //- add icon when we make an icon available
              ui-input-text(
                autocomplete="off"
                class="ui-select-filter"
                :placeholder="filterPlaceholder"
                ref="filterInput"
                role="searchbox"
                type="text"
                :value="filterValue"
                @blur="onFilterBlur"
                @input="onFilterChange"
                @keydown="onFilterKeyDown"
                @vue:mounted="onFilterUpdated"
                @vue:updated="onFilterUpdated"
              )
              span(
                aria-live="polite"
                class="p-hidden-accessible"
                :data-p-hidden-accessible="true"
                role="status"
              ) {{ filterResultMessageText }}
            div(
              :class="classes.listContainer"
              :style="{ 'max-height': virtualScrollerDisabled ? scrollHeight : '' }"
            )
              virtual-scroller(
                :disabled="virtualScrollerDisabled"
                :items="visibleOptions"
                :style="{ height: scrollHeight }"
                :ref="virtualScrollerRef"
                :tabindex="-1"
                v-bind="virtualScrollerOptions"
              )
                template(
                  v-slot:content="{ styleClass, contentRef, items, getItemOptions, contentStyle, itemSize }"
                )
                  ul(
                    :class="[classes.list, styleClass]"
                    :id="$id + '_list'"
                    :ref="(el) => listRef(el, contentRef)"
                    role="listbox"
                    :style="contentStyle"
                  )
                    template(
                      v-for="(option, i) of items"
                      :key="getOptionRenderKey(option, getOptionIndex(i, getItemOptions))"
                    )
                      li(
                        v-if="isOptionGroup(option)"
                        :class="classes.optionGroup"
                        :id="$id + '_' + getOptionIndex(i, getItemOptions)"
                        role="option"
                        :style="{ height: itemSize ? itemSize + 'px' : undefined }"
                      )
                        slot(
                          name="optiongroup"
                          :option="option.optionGroup"
                          :index="getOptionIndex(i, getItemOptions)"
                        )
                          span(
                            :class="classes.optionGroup"
                          ) {{ getOptionGroupLabel(option.optionGroup) }}
                      li(
                        v-else
                        :aria-disabled="isOptionDisabled(option)"
                        :aria-label="getOptionLabel(option)"
                        :aria-posinset="getAriaPosInset(getOptionIndex(i, getItemOptions))"
                        :aria-selected="isSelected(option)"
                        :aria-setsize="ariaSetSize"
                        :class="classes.option(option, getOptionIndex(i, getItemOptions))"
                        :data-ui-disabled="isOptionDisabled(option)"
                        :data-ui-focused="focusedOptionIndex === getOptionIndex(i, getItemOptions)"
                        :data-ui-selected="isSelected(option)"
                        :id="$id + '_' + getOptionIndex(i, getItemOptions)"
                        role="option"
                        :style="{ height: itemSize ? itemSize + 'px' : undefined }"
                        @click="onOptionSelect($event, option)"
                        @mousemove="onOptionMouseMove($event, getOptionIndex(i, getItemOptions))"
                      )
                        template(v-if="checkmark")
                          check-icon( v-if="isSelected(option)" class="select-check-icon")
                          blank-icon( v-else class="select-blank-icon")
                        slot(
                          :index="getOptionIndex(i, getItemOptions)"
                          name="option"
                          :option="option"
                          :selected="isSelected(option)"
                        )
                          span(:class="classes.optionLabel") {{ getOptionLabel(option) }}
                    li(
                      v-if="filterValue && (!items || (items && items.length === 0))"
                      :class="classes.emptyFilterMessage"
                      role="option"
                      :data-ui-hidden-accessible="true"
                    )
                      slot( name="emptyfilter") {{ emptyFilterMessageText }}
                    li(
                      v-else-if="!options || (options && options.length === 0)"
                      :class="classes.emptyMessage"
                      role="option"
                      :data-ui-hidden-accessible="true"
                    )
                      slot( name="empty") {{ emptyMessageText }}
                template(
                  v-if="$slots.loader"
                  v-slot:loader="{ options }"
                )
                  slot( name="loader" :options="options")
            span(
              v-if="!options || (options && options.length === 0)"
              aria-live="polite"
              class="ui-hidden-accessible"
              :data-ui-hidden-accessible="true"
              role="status"
            )  {{ emptyMessageText }}
            span(
              aria-live="polite"
              class="ui-hidden-accessible"
              :data-ui-hidden-accessible="true"
              role="status"
            )  {{ selectedMessageText }}
            span(
              aria-hidden="true"
              class="ui-hidden-accessible ui-hidden-focusable"
              :data-ui-hidden-accessible="true"
              :data-ui-hidden-focusable="true"
              ref="lastHiddenFocusableElementOnOverlay"
              role="presentation"
              :tabindex="0"
              @focus="onLastHiddenFocus"
            )
</template>


<script>
import {
 absolutePosition,
  addStyle,
  findSingle,
  focus,
  getFirstFocusableElement,
  getFocusableElements,
  getLastFocusableElement,
  getOuterWidth,
  isAndroid,
  isTouchDevice,
  relativePosition,
} from '../../utils/dom/index.js';
import {
  equals,
  findLastIndex,
  isNotEmpty,
  isPrintableCharacter,
  resolveFieldData,
} from '../../utils/object/index.js';
import { ZIndex } from '../../utils/zindex/zindex.js';
import { ConnectedOverlayScrollHandler } from '../../utils/core/connected-overlay-scroll-handler.js';
import { FilterService } from '../../core/api/filter-service.js';

// // TODO: Add icons!
import BlankIcon from '../icons/blank/blank.vue';
import CheckIcon from '../icons/check-heavy/check-heavy.vue';
import ChevronDownIcon from '../icons/double-chevron/double-chevron.vue';
import SearchIcon from '../icons/trash/trash.vue';
import SpinnerIcon from '../icons/clock/clock.vue';
import TimesIcon from '../icons/trash/trash.vue';
import InputIcon from  '../icons/trash/trash.vue';
import LoadingIcon from '../icons/clock/clock.vue';
import DropdownIcon from '../icons/double-chevron/double-chevron.vue';
// //
import VirtualScroller from '../virtual-scroller/virtual-scroller.vue';

import OverlayEventBus from '../../utils/eventbus/overlay-eventbus.js';
import BaseInput from '../base-input/base-input.vue';
import UiLabel from '../label/label.vue';
import { selectProps } from './select-props.js';
import UiPortal from '../portal/portal.vue';
// // TODO: Add/replace to get an icon on the input field?
import UiInputText from '../input-text/input-text.vue';

export default {
  name: 'UiSelect',

  components: {
    //
    BlankIcon,
    CheckIcon,
    ChevronDownIcon,
    DropdownIcon,
    SearchIcon,
    SpinnerIcon,
    TimesIcon,
    InputIcon,
    LoadingIcon,
    //
    UiInputText,
    UiLabel,
    UiPortal,
    VirtualScroller,
  },
  extends: BaseInput,
  inject: ['$ui'],
//   //
  isModelValueChanged: false,
  overlay: null,
  scrollHandler: null,
//   //
  props: selectProps,
  emits: ['change', 'focus', 'blur', 'before-show', 'before-hide', 'show', 'hide', 'filter'],
  data () {
    return {
      clicked: false,
      focused: false,
      focusedOptionIndex: -1,
      filterValue: null,
      isFilled: null,
      overlayVisible: false,
      queryOrientation: null, // used?
    };
  },
  computed: {
    classes() {
      return {
        root: [
          'ui-select ui-component ui-inputwrapper',
          {
            'ui-disabled': this.disabled,
            'ui-invalid': this.invalid,
            'ui-focus': this.focused || this.overlayVisible,
          }
        ],
        group: [
          'ui-select-group'
        ],
        header: 'ui-select-header',
        input: {},
        overlay: 'ui-select-overlay ui-component', // doesn't need to be computed!
        value: [
          'ui-select-value'
        ],
        listContainer: 'ui-select-list-container',
        list: 'ui-select-list',
        option: (option, focusedOption) => [
          'ui-select-option',
          {
            'ui-select-option-selected': this.isSelected(option) && this.highlightOnSelect,
            'ui-focus': this.focusedOptionIndex === focusedOption,
            'ui-disabled': this.isOptionDisabled(option),
          }
        ],
        optionGroup: ['ui-select-option-group'],
        optionLabel: {},
        emptyFilterMessage: {},
        emptyMessage: {},
      };
    },
    visibleOptions () {
      const options = this.optionGroupLabel ? this.flatOptions(this.options) : this.options || [];

      if (this.filterValue) {
        const filteredOptions = FilterService.filter(
          options,
          this.searchFields,
          this.filterValue,
          this.filterMatchMode,
          this.filterLocale
        );

        if (this.optionGroupLabel) {
          const optionGroups = this.options || [];
          const filtered = [];
          optionGroups.forEach((group) => {
            const groupChildren = this.getOptionGroupChildren(group);
            const filteredItems = groupChildren.filter((item) => filteredOptions.includes(item));
            if (filteredItems.length > 0) {
              filtered.push({
                ...group,
                [typeof this.optionGroupChildren === 'string' ? this.optionGroupChildren : 'items']: [...filteredItems]
              });
            }
          });
          return this.flatOptions(filtered);
        }
        return filteredOptions;
      }
      return options;
    },
    filterMessageText () {
      return this.filterMessage || this.$ui?.locale?.searchMessage || '';
    },
    // deprecated and replaced. Use this.isFilled
    // hasSelectedOption () {
    //   return this.$filled;
    // },
    displayValue () {
      const selectedOptionIndex = this.findSelectedOptionIndex();
      return selectedOptionIndex !== -1 ? this.getOptionLabel(this.visibleOptions[selectedOptionIndex]) : this.placeholder || this.$ui?.locale?.choose || 'EMPTY_LABEL';
    },
    editableInputValue () {
      const selectedOptionIndex = this.findSelectedOptionIndex();
      return selectedOptionIndex !== -1 ? this.getOptionLabel(this.visibleOptions[selectedOptionIndex]) : this.d_value || '';
    },
    equalityKey () {
      return this.optionValue ? null : this.dataKey;
    },
    searchFields () {
      return this.filterFields || [this.optionLabel];
    },
    filterResultMessageText () {
      return isNotEmpty(this.visibleOptions) ? this.filterMessageText.replaceAll('{0}', this.visibleOptions.length) : this.emptyFilterMessageText;
    },
    emptyFilterMessageText () {
      return this.emptyFilterMessage || this.$ui.locale.emptyFilterMessage || '';
    },
    emptyMessageText () {
      return this.emptyMessage || this.$ui.locale.emptyMessage || '';
    },
    selectionMessageText () {
      return this.selectionMessage || this.$ui.locale.selectionMessage || '';
    },
    emptySelectionMessageText () {
      return this.emptySelectionMessage || this.$ui.locale.emptySelectionMessage || '';
    },
    selectedMessageText () {
      return this.isFilled ? this.selectionMessageText.replaceAll('{0}', '1') : this.emptySelectionMessageText;
    },
    focusedOptionId () {
      return this.focusedOptionIndex !== -1 ? `${this.$id}_${this.focusedOptionIndex}` : null;
    },
    ariaSetSize () {
      return this.visibleOptions.filter((option) => !this.isOptionGroup(option)).length;
    },
    // isClearIconVisible () {
    //   return this.showClear && this.d_value != null && isNotEmpty(this.options);
    // },
    virtualScrollerDisabled () {
      return !this.virtualScrollerOptions;
    },
  },
  watch: {
    modelValue () {
      this.isModelValueChanged = true;
    },
    options () {
      this.autoUpdateModel();
    },
  },
  mounted () {
    this.autoUpdateModel();
    // this.bindLabelClickListener(); <-- don't need it.
    // this.bindMatchMediaOrientationListener(); <-- do I need it?
  },
  updated () {
    if (this.overlayVisible && this.isModelValueChanged)
      this.scrollInView(this.findSelectedOptionIndex());
    this.isModelValueChanged = false;
  },
  beforeUnmount () {
    this.unbindOutsideClickListener();
    this.unbindResizeListener();
    // this.bindLabelClickListener() <-- unneded it's in the component
    // this.bindMatchMediaOrientationListener() <--??

    if (this.scrollHandler) {
      this.scrollHandler.destroy();
      this.scrollHandler = null;
    }

    if (this.overlay) {
      ZIndex.clear(this.overlay);
      this.overlay = null;
    }
  },
  methods: {
    getOptionIndex (index, fn) {
      return this.virtualScrollerDisabled ? index: fn && fn(index)['index'];
    },
    getOptionLabel (option) {
      return this.optionLabel ? resolveFieldData(option, this.optionLabel) : option;
    },
    getOptionValue (option) {
      return this.optionValue ? resolveFieldData(option, this.optionValue) : option;
    },
    getOptionRenderKey (option, itemOptions, index, key) {
      return (this.dataKey ? resolveFieldData(option, this.dataKey) : this.getOptionLabel(option)) + '_' + index;
    },
    isOptionDisabled (option) {
      return this.optionDisabled ? resolveFieldData(option, this.optionDisabled) : false;
    },
    isOptionGroup (option) {
      return this.optionGroupLabel && option.optionGroup && option.group;
    },
    getOptionGroupLabel (optionGroup) {
      return resolveFieldData(optionGroup, this.optionGroupLabel);
    },
    getOptionGroupChildren (optionGroup) {
      return resolveFieldData(optionGroup, this.optionGroupChildren);
    },
    getAriaPosInset (index) {
      return (this.optionGroupLabel ? index - this.visibleOptions.slice(0, index).filter(option => this.isOptionGroup(option)).length : index) + 1;
    },
    show (isFocus) {
      this.$emit('before-show');
      this.overlayVisible = true;
      this.focusedOptionIndex = this.focusedOptionIndex !== -1 ? this.focusedOptionIndex
        : this.autoOptionFocus ? this.findFirstFocusedOptionIndex()
          : this.editable ? -1
            : this.findSelectedOptionIndex();

      isFocus && focus(this.$refs.focusInput);
    },
    hide (isFocus) {
      const _hide = () => {
        this.$emit('before-hide');
        this.overlayVisible = false;
        this.clicked = false;
        this.focusedOptionIndex = -1;
        this.searchValue = '';
        this.resetFilterOnHide && (this.filterValue = null);
        isFocus && focus(this.$refs.focusInput);
      };
      // for screen readers. This way we wait for them to catch up before hiding
      setTimeout(() => {_hide(); }, 0);
    },
    onFocus (event) {
      if (this.disabled) {
        return;
      }

      this.focused = true;

      if (this.overlayVisible) {
        this.focusedOptionIndex = this.focusedOptionIndex !== -1 ? this.focusedOptionIndex
          : this.autoOptionFocus ? this.findFirstFocusedOptionIndex()
            : this.editable ? -1
              : this.findFirstFocusedOptionIndex();
        this.scrollInView(this.focusedOptionIndex);
      }

      this.$emit('focus', event);
    },
    onBlur (event) {
      this.focused = false;
      this.focusedOptionIndex = -1;
      this.searchValue = '';
      this.$emit('blur', event);
      this.formField?.onBlur?.(event);
    },
    onKeyDown (event) {
      if (this.disabled || isAndroid()) {
        event.preventDefault();
        return;
      }
      const metaKey = event.metaKey || event.ctrlKey;

      switch (event.code) {
        case 'ArrowDown':
          this.onArrowDownKey(event);
          break;
        case 'ArrowUp':
          this.onArrowUpKey(event, this.editable);
          break;
        case 'ArrowLeft':
        case 'ArrowRight':
          this.onArrowLeftKey(event, this.editable);
          break;
        case 'Home':
          this.onHomeKey(event, this.editable);
          break;
        case 'End':
          this.onEndKey(event, this.editable);
          break;
        case 'PageDown':
          this.onPageDownKey(event);
          break;
        case 'PageUp':
          this.onPageUpKey(event);
          break;
        case 'Space':
          this.onSpaceKey(event);
          break;
        case 'Enter':
        case 'NumpadEnter':
          this.onEnterKey(event);
          break;
        case 'Escape':
          this.onEscapeKey(event);
          break;
        case 'Tab':
          this.onTabKey(event);
          break;
        case 'Backspace':
          this.onBackspaceKey(event, this.editable);
          break;
        case 'ShiftLeft':
        case 'ShiftRight':
          // NOOP
          break;
        default:
          if (!metaKey && isPrintableCharacter(event.key)) {
            !this.overlayVisible && this.show();
            !this.editable && this.searchOptions(event, event.key);
          }
          break;
      }

      this.clicked = false;
    },
    onEditableInput (event) {
      const value = event.target.value;

      this.searchValue = '';
      const matched = this.searchOptions(event, value);

      !matched && (this.focusedOptionIndex = -1);
      this.updateModel(event, value);

      !this.overlayVisible && isNotEmpty(value) && this.show();
    },
    onClickContainer (event) {
      if (this.disabled || this.loading) {
        return;
      }

      if (event.target.tagName === 'INPUT') // <removed clear icon>
        return;
      else if (!this.overlay || !this.overlay.contains(event.target))
        this.overlayVisible ? this.hide(true) : this.show(true);

      this.clicked = true;
    },
    onFirstHiddenFocus (event) {
      const focusableEl = event.relatedTarget === this.$refs.focusInput
        ? getFirstFocusableElement(this.overlay, ':not([data-ui-hidden-focusable="true"])')
        : this.$refs.focusInput;
      focus(focusableEl);
    },
    onLastHiddenFocus (event) {
      const focusableEl = event.relatedTarget === this.$refs.focusInput
        ? getLastFocusableElement(this.overlay, ':not([data-ui-hidden-focusable="true"])')
        : this.$refs.focusInput;
      focus(focusableEl);
    },
    onOptionSelect (event, option, isHide=true) {
      const value = this.getOptionValue(option);
      this.updateModel(event, value);
      isHide && this.hide(true);
      // TODO: Handle sorting here.
    },
    onOptionMouseMove (event, index) {
      if (this.focusOnHover) {
        this.changeFOcusedOptionIndex(event, index);
      }
    },
    onFilterChange (event) {
      const value = event.target.value;

      this.filterValue = value;
      this.focusedOptionIndex = -1;
      this.$emit('filter', {originalEvent: event, value});

      !this.virtualScrollerDisabled && this.virtualScroller.scrollToIndex(0);
    },
    onFilterKeyDown (event) {
      // dispatch key events in the filter input and dispatch them!

      if (event.isComposing) return;

      switch (event.code) {
      case 'ArrowDown':
        this.onArrowDownKey(event);
        break;
      case 'ArrowUp':
        this.onArrowUpKey(event, true);
        break;
      case 'ArrowLeft':
      case 'ArrowRight':
        this.onArrowLeftKey(event, true);
        break;
      case 'Home':
        this.onHomeKey(event, true);
        break;
      case 'End':
        this.onEndKey(event, true);
        break;
      case 'Enter':
      case 'NumpadEnter':
        this.onEnterKey(event);
        break;
      case 'Escape':
        this.onEscapeKey(event);
        break;
      case 'Tab':
        this.onTabKey(event, true);
        break;
      default:
        break;
      };
    },
    onFilterBlur () {
      this.focusedOptionIndex = -1;
    },
    onFilterUpdated () {
      if (this.overlayVisible)
        this.alignOverlay();
    },
    onOverlayClick (event) {
      OverlayEventBus.emit(
        'overlay-click',
        {originalEvent: event, target: this.$el}
      );
    },
    onOverlayKeyDown (event) {
      switch (event.code) {
      case 'Escape':
        this.onEscapeKey(event);
        break;
      default:
        break;
      };
    },
    onArrowDownKey (event) {
      if (!this.overlayVisible) {
        this.show();
        this.editable && this.changeFocusedOptionIndex(event, this.findSelectedOptionIndex());
      }
      else {
        const optionIndex = this.focusedOptionIndex !== -1 ? this.findNextOptionIndex(this.focusedOptionIndex)
          : this.clicked ? this.findFirstOptionIndex()
          : this.findFirstFocusedOptionIndex();
        this.changeFocusedOptionIndex(event, optionIndex);
      }

      event.preventDefault();
    },
    onArrowUpKey (event, pressedInInputText=false) {
      if (event.altKey && !pressedInInputText) {
        if (this.focusedOptionIndex !== -1) {
            this.onOptionSelect(event, this.visibleOptions[this.focusedOptionIndex]);
        }
        this.overlayVisible && this.hide();
      } else {
          const optionIndex = this.focusedOptionIndex !== -1 ? this.findPrevOptionIndex(this.focusedOptionIndex)
            : this.clicked ? this.findLastOptionIndex()
            : this.findLastFocusedOptionIndex();

          this.changeFocusedOptionIndex(event, optionIndex);

          !this.overlayVisible && this.show();
      }
      event.preventDefault();
    },
    onArrowLeftKey (event, pressedInInputText=false) {
      pressedInInputText && (this.focusedOptionIndex = -1);
    },
    onHomeKey (event, pressedInInputText=false) {
      if (pressedInInputText) {
        const target = event.currentTarget;
        if (event.shiftKey) {
          target.setSelectionRange(0, event.target.selectionStart);
        }
        else {
          target.setSelectionRange(0, 0);
          this.focusedOptionIndex = -1;
        }
      } else {
        this.changeFocusedOptionIndex(event, this.findFirstOptionIndex());
        !this.overlayVisible && this.show();
      }
      event.preventDefault();
    },
    onEndKey(event, pressedInInputText = false) {
      if (pressedInInputText) {
        const target = event.currentTarget;
        if (event.shiftKey) {
          target.setSelectionRange(event.target.selectionStart, target.value.length);
        } else {
          const len = target.value.length;
          target.setSelectionRange(len, len);
          this.focusedOptionIndex = -1;
        }
      } else {
        this.changeFocusedOptionIndex(event, this.findLastOptionIndex());
          !this.overlayVisible && this.show();
      }
      event.preventDefault();
    },
    onPageUpKey(event) {
      this.scrollInView(0);
      event.preventDefault();
    },
    onPageDownKey(event) {
      this.scrollInView(this.visibleOptions.length - 1);
      event.preventDefault();
    },
    onEnterKey(event) {
      if (!this.overlayVisible) {
        this.focusedOptionIndex = -1; // reset
        this.onArrowDownKey(event);
      }
      else {
        if (this.focusedOptionIndex !== -1) {
          this.onOptionSelect(event, this.visibleOptions[this.focusedOptionIndex]);
        }
        this.hide();
      }
      event.preventDefault();
    },
    onSpaceKey(event, pressedInInputText = false) {
      !pressedInInputText && this.onEnterKey(event);
    },
    onEscapeKey(event) {
      this.overlayVisible && this.hide(true);
      event.preventDefault();
      event.stopPropagation(); //@todo will be changed next versionss
    },
    onTabKey(event, pressedInInputText = false) {
      if (!pressedInInputText) {
        if (this.overlayVisible && this.hasFocusableElements()) {
          focus(this.$refs.firstHiddenFocusableElementOnOverlay);
          event.preventDefault();
        }
        else {
          if (this.focusedOptionIndex !== -1)
            this.onOptionSelect(event, this.visibleOptions[this.focusedOptionIndex]);
          this.overlayVisible && this.hide(this.filter);
        }
      }
    },
    onBackspaceKey(event, pressedInInputText = false) {
      if (pressedInInputText) {
        !this.overlayVisible && this.show();
      }
    },
    onOverlayEnter(el) {
      //  TODO: Set the overlay index globally/manually, rather than
      //  like this. WIll need *some* consideration, as this won't work
      //  out of the box.
      ZIndex.set('overlay', el, this.$ui.zIndex.overlay);

      addStyle(el, { position: 'absolute', top: '0', left: '0' });
      this.alignOverlay();
      this.scrollInView();

      // essentially nextTick... but fsater.
      setTimeout(() => {
        this.autoFilterFocus && this.filter && focus(this.$refs.filterInput.$el);
        this.autoUpdateModel();
      }, 1);
    },
    onOverlayAfterEnter() {
      // This ladies and gentlemen is how you save on the number
      // of event listeners, and prevent bogging down the UI. Dammit.
      this.bindOutsideClickListener();
      this.bindScrollListener();
      this.bindResizeListener();

      this.$emit('show');
    },
    onOverlayLeave() {
      this.unbindOutsideClickListener();
      this.unbindScrollListener();
      this.unbindResizeListener();

      if (this.autoFilterFocus && this.filter && !this.editable) {
        this.$nextTick(() => {
          if (this.$refs.filterInput) {
            focus(this.$refs.filterInput.$el);
          }
        });
      }

      this.$emit('hide');
      this.overlay = null;
    },
    onOverlayAfterLeave(el) {
      ZIndex.clear(el);
    },
    alignOverlay() {
      if (this.appendTo === 'self') {
        relativePosition(this.overlay, this.$el);
      }
      else if (this.overlay) {
        this.overlay.style.minWidth = getOuterWidth(this.$el) + 'px';
        absolutePosition(this.overlay, this.$el);
      }
    },
    bindOutsideClickListener() {
      if (!this.outsideClickListener) {
        this.outsideClickListener = (event) => {
          if (this.overlayVisible && this.overlay && !this.$el.contains(event.target) && !this.overlay.contains(event.target)) {
            this.hide();
          }
        };
        document.addEventListener('click', this.outsideClickListener, true);
      }
    },
    unbindOutsideClickListener() {
      if (this.outsideClickListener) {
        document.removeEventListener('click', this.outsideClickListener, true);
        this.outsideClickListener = null;
      }
    },
    bindScrollListener() {
      if (!this.scrollHandler) {
        this.scrollHandler = new ConnectedOverlayScrollHandler(this.$refs.container, () => {
          if (this.overlayVisible) {
            this.hide();
          }
        });
      }

      this.scrollHandler.bindScrollListener();
    },
    unbindScrollListener() {
      if (this.scrollHandler) {
          this.scrollHandler.unbindScrollListener();
      }
    },
    bindResizeListener() {
      if (!this.resizeListener) {
        this.resizeListener = () => {
          if (this.overlayVisible && !isTouchDevice()) {
            this.hide();
          }
        };

        window.addEventListener('resize', this.resizeListener);
      }
    },
    unbindResizeListener() {
      if (this.resizeListener) {
        window.removeEventListener('resize', this.resizeListener);
        this.resizeListener = null;
      }
    },
    bindMatchMediaOrientationListener() {
      if (!this.matchMediaOrientationListener) {
        const query = matchMedia('(orientation: portrait)');

        this.queryOrientation = query;

        this.matchMediaOrientationListener = () => {
          this.alignOverlay();
        };

        this.queryOrientation.addEventListener('change', this.matchMediaOrientationListener);
      }
    },
    unbindMatchMediaOrientationListener() {
      if (this.matchMediaOrientationListener) {
        this.queryOrientation.removeEventListener('change', this.matchMediaOrientationListener);
        this.queryOrientation = null;
        this.matchMediaOrientationListener = null;
      }
    },
    hasFocusableElements() {
      return getFocusableElements(this.overlay, ':not([data-ui-hidden-accessible="true"])').length > 0;
    },
    isOptionExactMatched(option) {
      // TODO: Consider using RegExp wiht /i instead of casting the case?
      return this.isValidOption(option)
        && typeof this.getOptionLabel(option) === 'string'
        && this.getOptionLabel(option)?.toLocaleLowerCase(this.filterLocale) == this.searchValue.toLocaleLowerCase(this.filterLocale);
    },
    isOptionStartsWith(option) {
      // TODO: Conside using RegExp with ^ and /i insetead of casting case?
      return this.isValidOption(option)
        && typeof this.getOptionLabel(option) === 'string'
        && this.getOptionLabel(option)?.toLocaleLowerCase(this.filterLocale).startsWith(this.searchValue.toLocaleLowerCase(this.filterLocale));
    },
    isValidOption(option) {
      return isNotEmpty(option) && !(this.isOptionDisabled(option) || this.isOptionGroup(option));
    },
    isValidSelectedOption(option) {
      return this.isValidOption(option) && this.isSelected(option);
    },
    isSelected(option) {
      return equals(this.d_value, this.getOptionValue(option), this.equalityKey);
    },
    findFirstOptionIndex() {
      return this.visibleOptions.findIndex((option) => this.isValidOption(option));
    },
    findLastOptionIndex() {
      return findLastIndex(this.visibleOptions, (option) => this.isValidOption(option));
    },
    findNextOptionIndex(index) {
      const matchedOptionIndex = index < this.visibleOptions.length - 1 ? this.visibleOptions.slice(index + 1).findIndex((option) => this.isValidOption(option)) : -1;
      return matchedOptionIndex > -1 ? matchedOptionIndex + index + 1 : index;
    },
    findPrevOptionIndex(index) {
      const matchedOptionIndex = index > 0 ? findLastIndex(this.visibleOptions.slice(0, index), (option) => this.isValidOption(option)) : -1;
      return matchedOptionIndex > -1 ? matchedOptionIndex : index;
    },
    findSelectedOptionIndex() {
      return this.$filled ? this.visibleOptions.findIndex((option) => this.isValidSelectedOption(option)) : -1;
    },
    findFirstFocusedOptionIndex() {
      const selectedIndex = this.findSelectedOptionIndex();
      return selectedIndex < 0 ? this.findFirstOptionIndex() : selectedIndex;
    },
    findLastFocusedOptionIndex() {
      const selectedIndex = this.findSelectedOptionIndex();
      return selectedIndex < 0 ? this.findLastOptionIndex() : selectedIndex;
    },
    searchOptions(event, char) {
      this.searchValue = (this.searchValue || '') + char;

      let optionIndex = -1;
      let matched = false;

      if (isNotEmpty(this.searchValue)) {
        optionIndex = this.visibleOptions.findIndex((option) => this.isOptionExactMatched(option));

        if (optionIndex === -1) {
          optionIndex = this.visibleOptions.findIndex((option) => this.isOptionStartsWith(option));
        }

        if (optionIndex !== -1) {
          matched = true;
        }

        if (optionIndex === -1 && this.focusedOptionIndex === -1) {
          optionIndex = this.findFirstFocusedOptionIndex();
        }

        if (optionIndex !== -1) {
          this.changeFocusedOptionIndex(event, optionIndex);
        }
      }

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(() => {
        this.searchValue = '';
        this.searchTimeout = null;
      }, 500);

      return matched;
    },
    changeFocusedOptionIndex(event, index) {
      if (this.focusedOptionIndex !== index) {
        this.focusedOptionIndex = index;
        this.scrollInView();
        if (this.selectOnFocus) {
          this.onOptionSelect(event, this.visibleOptions[index], false);
        }
      }
    },
    scrollInView(index = -1) {
      this.$nextTick(() => {
        const id = index !== -1 ? `${this.$id}_${index}` : this.focusedOptionId;
        const element = findSingle(this.list, `li[id="${id}"]`);

        if (element) {
          element.scrollIntoView && element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        } else if (!this.virtualScrollerDisabled) {
          this.virtualScroller && this.virtualScroller.scrollToIndex(index !== -1 ? index : this.focusedOptionIndex);
        }
      });
    },
    autoUpdateModel() {
      if (this.autoOptionFocus) {
        this.focusedOptionIndex = this.findFirstFocusedOptionIndex();
      }

      if (this.selectOnFocus && this.autoOptionFocus && !this.$filled) {
        this.onOptionSelect(null, this.visibleOptions[this.focusedOptionIndex], false);
      }
    },
    updateModel (event, value) {
      this.writeValue(value, event);
      this.$emit('change', { originalEvent: event, value });
    },
    flatOptions(options) {
      return (options || []).reduce((result, option, index) => {
        result.push({ optionGroup: option, group: true, index });
        const optionGroupChildren = this.getOptionGroupChildren(option);
        optionGroupChildren && optionGroupChildren.forEach((o) => result.push(o));
        return result;
      }, []);
    },
    overlayRef(el) {
      this.overlay = el;
    },
    listRef(el, contentRef) {
      this.list = el;
      contentRef && contentRef(el); // For VirtualScroller
    },
    virtualScrollerRef(el) {
      this.virtualScroller = el;
    },
  },

};

</script>
